import { useEffect, useState } from 'react'

import http from '../../../../../../shared/http'
import { TApi } from '../../../../../../shared/const'

import styles from './style.module.scss'
import { formatDate, formatVienna } from '../../../../../../shared/datetime'

/**
 * Der Änderungsverlauf der Einstellungen.
 *
 * Eine Einstellung gilt für die ganze Umgebung, ein Schuljahr dauert zehn
 * Monate — und in `Options` steht immer nur der letzte Stand. „Seit wann wird
 * der Kursnachweis für die Junior Basic Company verlangt?" war damit schlicht
 * nicht zu beantworten.
 *
 * Gezeigt wird, was der Server ohnehin mitschreibt: wer, wann, welche
 * Einstellung — und was die Anwendung daraufhin getan hat. Der letzte Punkt ist
 * der eigentliche Grund für die Liste: Beim Anheben der Quizhürde bleiben
 * bestandene Kapitel bestanden, beim Einschalten einer Voraussetzung behalten
 * aktivierte Junior Companies ihre Freischaltung. Beides geschieht von selbst;
 * hier steht, dass es geschehen ist.
 */

// Die Namen, wie sie in den Einstellungen darüber stehen. Ein Schlüssel ohne
// Eintrag wird unverändert gezeigt — lieber ein technischer Name als keiner.
const TITEL = {
	activationRequirements: 'Voraussetzungen für Aktivierung',
	conceptRequirement: 'Skills4Retail-Konzept',
	courseCertificateRequirement: 'Kursnachweis',
	hygieneTraining: 'Hygiene- und Allergenschulung',
	teacherDocumentUpload: 'Einverständniserklärung der Erziehungsberechtigten — Upload durch die Lehrkraft',
	requestChannel: 'Wer darf eine Anfrage stellen?',
	skills4RetailRequests: 'Zuständig für Skills4Retail-Anfragen',
	reportsDeadline: 'Fristen der Berichtsabgabe',
	invoicesOverdue: 'Rechnung überfällig ab',
	ticketTodoReminders: 'Erinnerung an offene Aufgaben'
}

const zeitpunkt = wert => {
	if (!wert) return '—'

	const datum = formatDate(wert, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})

	return `${datum}, ${formatVienna(wert, 'HH:mm')}`
}

export default function OptionChanges() {
	const [zeilen, setZeilen] = useState([])
	const [laedt, setLaedt] = useState(true)
	const [fehler, setFehler] = useState(null)

	useEffect(() => {
		let abgebrochen = false

		http.get(`${TApi.ADMIN_OPTION_CHANGES}?limit=25&offset=0`)
			.then(({ data }) => {
				if (abgebrochen) return
				setZeilen(data?.rows || [])
			})
			/*
			 * Scheitert die Abfrage, muss das dastehen. Eine leere Liste hieße
			 * „es hat nie jemand etwas geändert" — und das ist etwas anderes als
			 * „wir konnten nicht nachsehen".
			 */
			.catch(() => !abgebrochen && setFehler('Der Änderungsverlauf lässt sich gerade nicht laden.'))
			.finally(() => !abgebrochen && setLaedt(false))

		return () => {
			abgebrochen = true
		}
	}, [])

	return (
		/*
		 * Ohne eigene Überschrift und ohne eigene Einleitung: Beides steht in
		 * der Gruppe darüber (`GROUPS` in Settings/index.jsx) und stand hier
		 * ein zweites Mal — zwei Zeilen „Änderungsverlauf" untereinander, mit
		 * fast demselben Satz darunter.
		 */
		<div className={styles.root}>
			{laedt && <p className={styles.hint}>Wird geladen …</p>}
			{fehler && <p className={styles.error}>{fehler}</p>}

			{!laedt && !fehler && zeilen.length === 0 && (
				<p className={styles.hint}>Seit Einführung des Verlaufs wurde keine Einstellung geändert.</p>
			)}

			{zeilen.length > 0 && (
				/*
				 * Eine Liste und keine Tabelle: Der Inhaltsbereich der Einstellungen
				 * ist neben dem Untermenü rund 630 px breit. Vier Spalten liessen
				 * für die Wirkung gut 200 px übrig, und dort stehen ganze Sätze —
				 * sie standen als schmale Säule aus Einzelwörtern da. Zeitpunkt,
				 * Einstellung und Person gehören ohnehin in eine Zeile: Sie
				 * beantworten zusammen die eine Frage „wer wann was".
				 */
				<ul className={styles.list}>
					{zeilen.map(zeile => (
						<li key={zeile.id} className={styles.entry}>
							<p className={styles.head}>
								<b>{TITEL[zeile.name] || zeile.name}</b>
								<span className={styles.meta}>
									{zeitpunkt(zeile.createdAt)}
									{zeile.changedByName ? ` · ${zeile.changedByName}` : ''}
								</span>
							</p>

							{(zeile.effects || []).length === 0 ? (
								<p className={styles.plain}>Nichts verschärft — die Änderung wirkt sofort für alle.</p>
							) : (
								<ul className={styles.effects}>
									{zeile.effects.map((satz, index) => (
										<li key={index}>{satz}</li>
									))}
								</ul>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
